import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, CheckCheck, Radar } from "lucide-react";

export function NotificationBell({ variant = "light" }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  const loadCount = () => api.get("/notifications/unread-count").then((r) => setUnread(r.data.count)).catch(() => {});
  const loadList = () => api.get("/notifications").then((r) => setItems(r.data)).catch(() => {});

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 60000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => { if (open) loadList(); }, [open]);

  const openItem = async (n) => {
    if (!n.read) { await api.post(`/notifications/${n.id}/read`); }
    setOpen(false); loadCount();
    if (n.link) nav(n.link);
  };
  const markAll = async () => { await api.post("/notifications/read-all"); loadCount(); loadList(); };

  const iconColor = variant === "light" ? "text-white/70 hover:text-white" : "text-foreground";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className={`relative inline-flex items-center justify-center w-11 h-11 rounded-md transition-colors ${variant === "light" ? "hover:bg-white/10" : "hover:bg-secondary"}`} aria-label="Notifications" data-testid="notification-bell">
          <Bell className={`w-5 h-5 ${iconColor}`} />
          {unread > 0 && <span className="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-accent text-accent-foreground text-[10px] font-bold flex items-center justify-center" data-testid="notification-count">{unread > 9 ? "9+" : unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <span className="font-display font-bold text-sm">Notifications</span>
          {items.some((i) => !i.read) && <button onClick={markAll} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="notification-mark-all"><CheckCheck className="w-3.5 h-3.5" /> Mark all read</button>}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground">You're all caught up.</div>
          ) : items.map((n) => (
            <button key={n.id} onClick={() => openItem(n)} data-testid={`notification-${n.id}`} className={`w-full text-left px-4 py-3 border-b border-border last:border-0 hover:bg-secondary/60 transition-colors flex gap-3 ${!n.read ? "bg-accent/5" : ""}`}>
              <span className="mt-0.5 shrink-0"><Radar className="w-4 h-4 text-accent" /></span>
              <span className="min-w-0">
                <span className="block text-sm font-medium truncate">{n.title}</span>
                <span className="block text-xs text-muted-foreground">{n.body}</span>
                <span className="block text-[10px] text-muted-foreground mt-0.5">{n.created_at ? new Date(n.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : ""}</span>
              </span>
              {!n.read && <span className="ml-auto mt-1 w-2 h-2 rounded-full bg-accent shrink-0" />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
